/**
 * Filtered Grid — AJAX "Load More" + filter interactions.
 *
 * Reads configuration from data-* attributes on .js-filtered-grid.
 * On filter change or Load More click, fetches card HTML from the
 * rd_filtered_grid AJAX endpoint and updates the DOM.
 */

import { gsap } from 'gsap';

/**
 * Collect filter values from the grid's filter form.
 *
 * @param {HTMLFormElement|null} formEl
 * @return {Array<{taxonomy: string, slugs: string[], type: string}>}
 */
function collectFilters(formEl) {
	if (!formEl) {
		return [];
	}

	const grouped = {};
	const selects = formEl.querySelectorAll('.filter-select');

	selects.forEach((select) => {
		const value = select.value;
		if (!value) {
			return;
		}

		const taxonomy = select.dataset.taxonomy || '';
		const filterType = taxonomy ? 'taxonomy' : 'localita';
		const key = taxonomy || 'localita';

		if (!grouped[key]) {
			grouped[key] = {
				type: filterType,
				taxonomy: taxonomy,
				values: [],
			};
		}

		grouped[key].values.push(String(value));
	});

	return Object.entries(grouped).map(([key, info]) => ({
		taxonomy: key,
		slugs: info.values,
		type: info.type,
	}));
}

/**
 * Fetch grid results via AJAX.
 *
 * @param {Object} opts
 * @return {Promise<{html: string, totalPages: number, foundPosts: number}|null>}
 */
async function fetchGrid({
	postType,
	cardType,
	nonce,
	perPage,
	constraintTaxonomy,
	constraintTermId,
	filters,
	paged,
	randSeed,
	resultsEl,
	gridEl,
}) {
	const payload = new URLSearchParams();
	payload.set('action', 'rd_filtered_grid');
	payload.set('nonce', nonce || '');
	payload.set('post_type', postType || '');
	payload.set('card_type', cardType || '');
	payload.set('per_page', String(perPage || 9));
	payload.set('paged', String(paged || 1));
	payload.set('filters', JSON.stringify(filters || []));
	payload.set('constraint_taxonomy', constraintTaxonomy || '');
	payload.set('constraint_term_id', String(constraintTermId || 0));
	payload.set('rand_seed', String(randSeed || 0));

	try {
		const response = await fetch(rdScripts.ajaxUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
			},
			body: payload.toString(),
		});

		const data = await response.json();

		if (data && data.success && data.data && typeof data.data.html === 'string') {
			return {
				html: data.data.html,
				totalPages: data.data.total_pages || 1,
				foundPosts: data.data.found_posts || 0,
			};
		}

		return null;
	} catch (_error) {
		// Keep current HTML if request fails.
		return null;
	}
}

/**
 * Initialize all filtered grids on the page.
 *
 * @param {ParentNode} root
 */
export function initFilteredGrid(root = document) {
	const grids = root.querySelectorAll('.js-filtered-grid');

	grids.forEach((grid) => {
		if (grid.dataset.initialized === '1') {
			return;
		}
		grid.dataset.initialized = '1';

		// --- Read config from data attributes ---
		const postType = grid.dataset.postType || '';
		const cardType = grid.dataset.cardType || '';
		const nonce = grid.dataset.nonce || '';
		const perPage = Number.parseInt(grid.dataset.perPage || '9', 10);
		const constraintTaxonomy = grid.dataset.constraintTaxonomy || '';
		const constraintTermId = Number.parseInt(grid.dataset.constraintTermId || '0', 10);
		const randSeed = Number.parseInt(grid.dataset.randSeed || '0', 10);

		// --- Find child elements ---
		const formEl = grid.querySelector('.js-filtered-grid-form');
		const panelEl = grid.querySelector('.js-filtered-grid-panel');
		const toggleBtn = grid.querySelector('.js-filtered-grid-toggle');
		const countEl = grid.querySelector('.js-filtered-grid-count');
		const applyBtn = grid.querySelector('.js-filtered-grid-apply');
		const resetBtn = grid.querySelector('.js-filtered-grid-reset');
		const resultsContainer = grid.querySelector('.js-filtered-grid-results');
		const cardsContainer = grid.querySelector('.js-filtered-grid-cards');
		const loadMoreWrap = grid.querySelector('.js-filtered-grid-loadmore');
		const loadMoreBtn = grid.querySelector('.js-filtered-grid-loadmore-btn');

		if (!resultsContainer || !cardsContainer) {
			return;
		}

		let nextPage = loadMoreBtn ? Number.parseInt(loadMoreBtn.dataset.nextPage || '2', 10) : 2;
		let totalPages = nextPage > 2 ? Number.MAX_SAFE_INTEGER : 1;
		let isLoading = false;

		// Recompute total pages once we know from the first AJAX response.
		const updateLoadMoreVisibility = () => {
			if (!loadMoreWrap) {
				return;
			}
			if (nextPage > totalPages) {
				loadMoreWrap.style.display = 'none';
			} else {
				loadMoreWrap.style.display = '';
			}
		};

		// --- Fetch and update ---
		const request = async (page, append = false) => {
			if (isLoading) {
				return;
			}
			isLoading = true;
			grid.classList.add('is-loading');
			// Only show the overlay when replacing the whole grid (filter change/reset),
			// not when appending the next page of results.
			if (!append) {
				grid.classList.add('is-replacing');
			}

			// Show spinner on the Load More button.
			if (loadMoreBtn) {
				loadMoreBtn.dataset.label = loadMoreBtn.innerHTML;
				loadMoreBtn.disabled = true;
				loadMoreBtn.classList.add('is-loading');
				loadMoreBtn.innerHTML = '<span class="btn-load-more__spinner"></span>';
			}

			const filters = collectFilters(formEl);

			const result = await fetchGrid({
				postType,
				cardType,
				nonce,
				perPage,
				constraintTaxonomy,
				constraintTermId,
				filters,
				paged: page,
				randSeed,
				resultsEl: resultsContainer,
				gridEl: grid,
			});

			if (result) {
				if (append) {
					// Append new cards with entrance animation.
					const tmp = document.createElement('div');
					tmp.innerHTML = result.html;
					const newCards = Array.from(tmp.querySelectorAll('.col-12'));

					// Start hidden, then animate in on next frame.
					newCards.forEach((card) => {
						card.classList.add('is-appearing');
						cardsContainer.appendChild(card);
					});

					// Trigger animation after elements are in the DOM.
					requestAnimationFrame(() => {
						newCards.forEach((card) => {
							card.classList.add('is-visible');
							card.classList.remove('is-appearing');
						});
					});

					nextPage = page + 1;
				} else {
					// Replace all cards.
					cardsContainer.innerHTML = result.html;
					nextPage = 2;
				}
				totalPages = result.totalPages;
				updateLoadMoreVisibility();
			}

			isLoading = false;
			grid.classList.remove('is-loading');
			grid.classList.remove('is-replacing');
			if (loadMoreBtn) {
				loadMoreBtn.classList.remove('is-loading');
				loadMoreBtn.disabled = false;
				loadMoreBtn.innerHTML = loadMoreBtn.dataset.label || loadMoreBtn.textContent;
			}
		};

		// --- GSAP smooth-height filter panel toggle ---
		let isFiltersOpen = false;

		if (toggleBtn && panelEl) {
			toggleBtn.addEventListener('click', () => {
				if (!isFiltersOpen) {
					gsap.to(panelEl, {
						height: 'auto',
						opacity: 1,
						duration: 0.45,
						ease: 'power2.out',
					});
					toggleBtn.classList.add('active');
					isFiltersOpen = true;
				} else {
					gsap.to(panelEl, {
						height: 0,
						opacity: 0,
						duration: 0.35,
						ease: 'power2.inOut',
					});
					toggleBtn.classList.remove('active');
					isFiltersOpen = false;
				}
			});
		}

		// --- Filter apply (submit) ---
		if (formEl) {
			formEl.addEventListener('submit', (event) => {
				event.preventDefault();
				request(1, false);
				if (isFiltersOpen && toggleBtn && panelEl) {
					gsap.to(panelEl, {
						height: 0,
						opacity: 0,
						duration: 0.35,
						ease: 'power2.inOut',
					});
					toggleBtn.classList.remove('active');
					isFiltersOpen = false;
				}
			});
		}

		// --- Filter reset ---
		if (resetBtn && formEl) {
			resetBtn.addEventListener('click', () => {
				const selects = formEl.querySelectorAll('.filter-select');
				selects.forEach((select) => {
					select.value = '';
				});
				request(1, false);
				if (isFiltersOpen && toggleBtn && panelEl) {
					gsap.to(panelEl, {
						height: 0,
						opacity: 0,
						duration: 0.35,
						ease: 'power2.inOut',
					});
					toggleBtn.classList.remove('active');
					isFiltersOpen = false;
				}
			});
		}

		// --- Load More ---
		if (loadMoreBtn) {
			loadMoreBtn.addEventListener('click', () => {
				request(nextPage, true);
			});
		}

		// --- Update filter count badge ---
		const updateFilterCount = () => {
			if (!countEl || !formEl) {
				return;
			}
			const selects = formEl.querySelectorAll('.filter-select');
			let count = 0;
			selects.forEach((select) => {
				if (select.value) {
					count++;
				}
			});
			countEl.textContent = String(count);
			countEl.hidden = count < 1;
		};

		if (formEl) {
			formEl.addEventListener('change', updateFilterCount);
		}
		updateFilterCount();
	});
}
